import { useMemo, useState } from 'react';
import { MdOutlineDelete } from 'react-icons/md';
import { IconButton, Tab, Tabs, Tooltip } from '@ds';
import { FilterDropdown } from '@ui';
import { ROUTE_FILTER_TYPE_LIST, ROUTE_FILTER_TYPE_TITLE_MAP } from '@lp/features/RoutePlanner/config';
import { useLogisticsActions } from '@lp/features/RoutePlanner/hooks/useLogisticsActions';
import { useRoutePlannerData } from '@lp/features/RoutePlanner/contexts/RoutePlannerDataContext';
import { RouteProvider } from '@lp/core/contexts/RouteContext';
import { AircraftUtilizationProvider } from '@lp/features/RoutePlanner/contexts/AircraftUtilizationContext';
import { useAuthContext } from '@lp/core/auth';
import { CaseStatus, RoutePlanStatus } from '@/api/apis/case';
import { RouteTypeEnum } from '@/api/apis/route';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { isAllowedStatus } from '@/utils/helpers';
import { RouteBuilderPermissions } from '@/permissions';
import { RouteFilterType } from '@/types/route';
import { RouteList } from './RouteList';

enum LegListTabs {
  main = 'main',
  dryRun = 'dryRun',
}

export const LegList = () => {
  const [filterType, setFilterType] = useState<RouteFilterType>(RouteFilterType.Full);
  const [isClearInProgress, setIsClearInProgress] = useState(false);
  const { checkPermission } = useAuthContext();
  const { routePlanningStatus, status } = useBaseCaseDetails();
  const {
    mainRoute,
    dryRunRoute,
    locations,
    resources,
    donorHospitalDateTimeString,
    donorOperationEndDateTimeString,
    recipientHospitalDateTimeString,
    recipientOperationEndDateTimeString,
  } = useRoutePlannerData();
  const { handleClearUnfinishedLegs } = useLogisticsActions();

  const isDeleteAllowed = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.LegDelete);
    const isFullRoute = filterType === RouteFilterType.Full;
    const hasLegs = !![...mainRoute.legs, ...dryRunRoute.legs].length;
    const hasHubs = !!(mainRoute.departureHub || mainRoute.arrivalHub || dryRunRoute.arrivalHub);
    const hasDataToRemove = hasLegs || hasHubs;
    const correctStatus = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isFullRoute && hasDataToRemove && correctStatus && isActiveCaseStatus;
  }, [status, routePlanningStatus, mainRoute, dryRunRoute, checkPermission, filterType]);

  const handleClearRoute = async () => {
    try {
      setIsClearInProgress(true);
      await handleClearUnfinishedLegs();
    } finally {
      setIsClearInProgress(false);
    }
  };

  return (
    <RouteProvider locations={locations} resources={resources}>
      <AircraftUtilizationProvider>
        <div className="relative w-full">
          <Tabs
            aria-label="Route Tabs"
            variant="underlined"
            classNames={{
              tabContent: 'text-m-cps',
              base: 'w-full',
              tabList: 'p-0 flex w-96',
              tab: 'basis-1/2',
              panel: 'w-full h-full',
            }}
          >
            <Tab key={LegListTabs.main} title="Main Route">
              <RouteList
                routeType={RouteTypeEnum.Main}
                filterType={filterType}
                donorHospitalDateTimeString={donorHospitalDateTimeString}
                recipientHospitalDateTimeString={recipientHospitalDateTimeString}
                donorOperationEndDateTimeString={donorOperationEndDateTimeString}
                recipientOperationEndDateTimeString={recipientOperationEndDateTimeString}
              />
            </Tab>
            <Tab key={LegListTabs.dryRun} title="Dry Run">
              <RouteList
                routeType={RouteTypeEnum.DryRun}
                filterType={filterType}
                donorHospitalDateTimeString={donorHospitalDateTimeString}
              />
            </Tab>
          </Tabs>
          <div className="absolute right-4 top-1 flex gap-2 items-center">
            <FilterDropdown
              title={ROUTE_FILTER_TYPE_TITLE_MAP[filterType]}
              items={ROUTE_FILTER_TYPE_LIST}
              selectedKeys={[filterType]}
              mode="single"
              size="sm"
              showCounter={false}
              disallowEmptySelection
              onSelectedKeysChange={(selected) => setFilterType(selected[0] as RouteFilterType)}
            />
            {isDeleteAllowed && (
              <Tooltip content="Clear unfinished legs" closeDelay={0}>
                <IconButton
                  isLoading={isClearInProgress}
                  isDisabled={isClearInProgress}
                  size="sm"
                  variant="light"
                  onPress={handleClearRoute}
                  startContent={
                    <div className="text-base-5">
                      <MdOutlineDelete size={18} />
                    </div>
                  }
                />
              </Tooltip>
            )}
          </div>
        </div>
      </AircraftUtilizationProvider>
    </RouteProvider>
  );
};
